import {
  CUSTOMER_ADDRESS1_MAX_LENGTH,
  CUSTOMER_ADDRESS2_MAX_LENGTH,
  CUSTOMER_ATTENTION_NAME_MAX_LENGTH,
  CUSTOMER_CITY_MAX_LENGTH,
  CUSTOMER_NAME_MAX_LENGTH,
  CUSTOMER_POSTAL_CODE_MAX_LENGTH,
  CUSTOMER_STATE_MAX_LENGTH,
} from '@snap-types/models/customer';
import { wonLossStatusSchema } from '@snap-types/models/enums';
import type {
  QuoteEditableNumberFields,
  QuoteEditableSelectFields,
  QuoteEditableStringFields,
} from '@ui/features/quote-builder/types/state/quote';
import { z } from 'zod/v4';

const nonNegativeNumberValidator = z
  .number({
    error: 'Number is required',
  })
  .nonnegative({ error: 'Cannot be negative' });

export const quoteBuilderFieldValidators = {
  name: z
    .string({
      error: 'Customer name is required',
    })
    .trim()
    .nonempty({ error: 'Customer name is required' })
    .max(CUSTOMER_NAME_MAX_LENGTH, {
      error: `Customer name cannot exceed ${CUSTOMER_NAME_MAX_LENGTH} characters`,
    }),
  salesRepId: z.string().nullable(),
  csrOid: z.string().nullable(),
  contactName: z
    .string({ error: 'Attention name is required' })
    .trim()
    .nonempty({ error: 'Attention name is required' })
    .max(CUSTOMER_ATTENTION_NAME_MAX_LENGTH, {
      error: `Attention name cannot exceed ${CUSTOMER_ATTENTION_NAME_MAX_LENGTH} characters`,
    }),
  contactEmail: z
    .email({ error: 'Email is invalid', pattern: z.regexes.html5Email })
    .trim(),
  contactPhone: z.string().nullable(),
  address1: z
    .string()
    .max(CUSTOMER_ADDRESS1_MAX_LENGTH, {
      error: `Address 1 cannot exceed ${CUSTOMER_ADDRESS1_MAX_LENGTH} characters`,
    })
    .nullable(),
  address2: z
    .string()
    .max(CUSTOMER_ADDRESS2_MAX_LENGTH, {
      error: `Address 2 cannot exceed ${CUSTOMER_ADDRESS2_MAX_LENGTH} characters`,
    })
    .nullable(),
  city: z
    .string()
    .max(CUSTOMER_CITY_MAX_LENGTH, {
      error: `City cannot exceed ${CUSTOMER_CITY_MAX_LENGTH} characters`,
    })
    .nullable(),
  state: z
    .string()
    .max(CUSTOMER_STATE_MAX_LENGTH, {
      error: `State cannot exceed ${CUSTOMER_STATE_MAX_LENGTH} characters`,
    })
    .nullable(),
  postalCode: z
    .string()
    .max(CUSTOMER_POSTAL_CODE_MAX_LENGTH, {
      error: `Postal code cannot exceed ${CUSTOMER_POSTAL_CODE_MAX_LENGTH} characters`,
    })
    .nullable(),
  countryCode: z.string().nullable(),
  specs: z.string().nullable(),
  notes: z.string().nullable(),
  customerQuoteId: z.string().nullable(),
  daysUntilQuoteExpires: nonNegativeNumberValidator,
  daysUntilExpectedWin: nonNegativeNumberValidator,
  wonLossStatus: wonLossStatusSchema.nullable(),
  // TODO: When these are in fact editable, update the validation
  discountCode: z.string().nullable(),
  marketCode: z.string().nullable(),
} as const satisfies Record<
  QuoteEditableNumberFields &
    QuoteEditableSelectFields &
    QuoteEditableStringFields,
  z.ZodTypeAny
>;

export const quoteBuilderSchema = z.object(quoteBuilderFieldValidators);
